import { FC, useCallback, useMemo, useState } from "react"
import { FieldProps } from "@rjsf/core"
import { JSONSchema7 } from "json-schema"
import { Table, TableColumn } from "resha"
import { Title, Button } from "dash-styled"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"
import { ReactComponent as DragIcon } from "icons/lib/drag.svg"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"

import {
    TableHeaderModal,
    TableHeaderModalType
} from "../DocumentsExtraction/Common/Modals/TableHeaderModal"

export interface TableHeader {
    id: string
    title: string
    options: {
        type: string
    }
    required: boolean
    aliaces?: string[]
}

type ModalOptions = {
    index?: number
    type: TableHeaderModalType
}

export const TableHeadersField: FC<FieldProps<TableHeader[] | undefined>> =
    fieldProps => {
        const {
            formData = [],
            schema,
            registry,
            onChange,
            errorSchema
        } = fieldProps

        const [modalOptions, setModalOptions] = useState<ModalOptions>()
        const rootSchema = registry.rootSchema

        const handleAddHeader = useCallback(() => {
            setModalOptions({ type: TableHeaderModalType.New })
        }, [])

        const externalIDsForModal = useMemo(() => {
            if (!modalOptions) {
                return []
            }
            return formData
                .map(header => header.id)
                .filter(
                    (_, idx) =>
                        modalOptions.type === TableHeaderModalType.New ||
                        modalOptions.index !== idx
                )
        }, [modalOptions, formData])

        const handleSubmit = useCallback(
            (header: TableHeader) => {
                const index = modalOptions?.index ?? formData.length
                const newFormData = [...formData]
                newFormData[index] = header
                onChange(newFormData)
            },
            [formData, onChange, modalOptions]
        )

        const handleDrop = (items: TableHeader[]) => {
            onChange(items)
        }

        const columns = useMemo<TableColumn<TableHeader>[]>(
            () => [
                {
                    className: (item, isChecked, index) =>
                        `w-1/12 pl-2 bg-white border-b-rhino-300 ${
                            errorSchema[index] ? `bg-blood-600` : ``
                        }`,
                    headerClassName: "hidden",
                    renderHeader: () => null,
                    render: () => {
                        return <DragIcon className="w-8 h-8" />
                    }
                },
                {
                    className: (item, isChecked, index) =>
                        `pl-2 bg-white border-b-rhino-300 ${
                            errorSchema[index] ? `bg-blood-600` : ``
                        }`,
                    renderHeader: () => null,
                    headerClassName: "hidden",
                    render: ({ title }) => {
                        return (
                            <>
                                <Title
                                    component="h4"
                                    variant="heading4"
                                    className="capitalize m-0"
                                >
                                    {title}
                                </Title>
                            </>
                        )
                    }
                },
                {
                    className: (item, isChecked, index) =>
                        `w-32 bg-white border-b-rhino-300 ${
                            errorSchema[index] ? `bg-blood-600` : ``
                        }`,
                    headerClassName: "hidden",
                    renderHeader: () => null,
                    render: tableHeader => {
                        return <div>{tableHeader.options.type}</div>
                    }
                },
                {
                    headerClassName: "hidden",
                    className: (item, isChecked, index) =>
                        `w-2/12 bg-white border-b-rhino-300 ${
                            errorSchema[index] ? `bg-blood-600` : ``
                        }`,
                    renderHeader: () => null,
                    render: (
                        value: TableHeader,
                        isChecked: boolean,
                        index: number
                    ) => {
                        return (
                            <div className="flex items-center justify-end border-l">
                                <div
                                    className="p-2 cursor-pointer"
                                    onClick={() => {
                                        setModalOptions({
                                            index,
                                            type: TableHeaderModalType.Edit
                                        })
                                    }}
                                >
                                    <EditIcon className="w-4 h-4" />
                                </div>
                                <div
                                    className="p-2 cursor-pointer"
                                    onClick={() => {
                                        onChange(
                                            formData.filter(
                                                (_, idx) => idx !== index
                                            )
                                        )
                                    }}
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </div>
                            </div>
                        )
                    }
                }
            ],
            [onChange, formData, errorSchema]
        )

        return (
            <div>
                <Title variant="heading3" weight="regular">
                    Table Headers{" "}
                    {formData.length > 0 ? `(${formData.length})` : ""}
                </Title>
                <Table
                    className="w-full border-t border-t-rhino-300"
                    draggable
                    getKey={({ id }) => id}
                    columns={columns}
                    items={formData}
                    onDrop={handleDrop}
                />
                <Button
                    className="mt-4 flex items-center"
                    size="xs"
                    color="violet"
                    variant="text"
                    onClick={handleAddHeader}
                >
                    <AddIcon className="mr-2" />
                    Add new header
                </Button>
                {modalOptions && (
                    <TableHeaderModal
                        onClose={() => {
                            setModalOptions(undefined)
                        }}
                        onSubmit={handleSubmit}
                        schema={schema?.items as JSONSchema7}
                        rootSchema={rootSchema}
                        fieldProps={fieldProps}
                        type={modalOptions.type}
                        value={
                            modalOptions.index === undefined
                                ? undefined
                                : formData[modalOptions.index]
                        }
                        externalIds={externalIDsForModal}
                    />
                )}
            </div>
        )
    }

// export default TableHeadersField
